/* eslint-disable no-console */
import { sendEnterAction, sendLeaveAction } from 'lib-node-observability';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';

import { type MetaDataDyna, useDynaTracking } from './useDynaTracking';

type MetaData = {
  category: string;
  description?: string;
};

// Mock dtrum object for non-production environments
if (import.meta.env.DEV) {
  globalThis.dtrum = {
    enterAction: (action: string) => {
      console.group?.('enterAction');
      console.log(action);
      console?.groupEnd();
      return Math.random();
    },
    leaveAction: (actionId: number) => {
      console.group?.('leaveAction');
      console.log(actionId);
      console.groupEnd?.();
    },
    reportError: (error: Error) => {
      console.log(error);
    },
  };
}

const CATEGORY_MAP = Object.freeze({
  0: 'classify',
  1: 'borrower',
  2: 'liabilities',
  3: 'assets',
  4: 'income',
  5: 'SubjectProperty',
  6: 'final review',
  8: 'uw_final_review',
  10: 'fha',
  11: 'va',
  12: 'appraisal',
  21: 'status_review',
});

const checkpoints = new Proxy(CATEGORY_MAP, {
  get(target, prop: string | symbol) {
    if (!target[prop as unknown as keyof typeof CATEGORY_MAP]) {
      console.error('unknown categoryId sent:', prop);
      return `unknown categoryId: ${String(prop)}`;
    }
    return target[prop as unknown as keyof typeof CATEGORY_MAP];
  },
});
/**
 * @description This hook is used to track the user's progress through the work items in the application. It does not
 * throw any errors but logs them because this shouldn't impact the user experience.
 */
const useWorkTracking = () => {
  const { sendEnterAndLeaveActionDyna } = useDynaTracking();
  const [parentWorkId, setParentWorkId] = useState<number | null>(null);

  const { selectedCategory, selectedAlert, selectedAlertParent, user } =
    useSelector(({ home }: { home: any }) => home);
  const { data: loanSummary } = useGetLoanSummary();
  const parentDescription =
    selectedAlertParent?.workTrackingTitle ?? selectedAlertParent?.displayTitle;
  const childDescription = selectedAlert?.displayTitle;
  const isSameTitle =
    selectedAlert?.displayTitle === selectedAlertParent?.displayTitle;
  // Only access checkpoints if selectedCategory is a valid number to prevent proxy errors
  const category =
    typeof selectedCategory === 'number' ? checkpoints[selectedCategory] : '';
  const action = isSameTitle
    ? `category-${category}-ChildItem-${childDescription}`
    : `category-${category}-ParentItem-${parentDescription}-ChildItem-${childDescription}`;

  const metaDataDyna = {
    category,
    loanNumber: loanSummary?.loanInfo?.loanNumber,
    role: user?.role,
    email: user?.email,
    parentItemTitle: isSameTitle ? '' : parentDescription,
    childItemTitle: childDescription,
    action,
  } as MetaDataDyna;

  const metaData: MetaData = {
    category,
    description: parentDescription ?? childDescription,
  };

  useEffect(() => {
    if (!selectedCategory || !selectedAlertParent) return;
    sendEnterAndLeaveAction(
      'parent work item',
      parentWorkId,
      setParentWorkId,
      metaData,
    );
    sendEnterAndLeaveActionDyna(metaDataDyna);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedAlertParent]);

  useEffect(() => {
    if (!selectedAlert) return;
    const gutterItemChild = selectedAlert?.gutterItemId;
    const gutterItemParent = selectedAlertParent?.gutterItemChildren;

    // Find the object in the array whose gutterItemId matches gutterItemChild
    const foundGutterItem = Array.isArray(gutterItemParent)
      ? gutterItemParent.find(
          (item: any) => item.gutterItemId === gutterItemChild,
        )
      : undefined;

    if (foundGutterItem) {
      sendEnterAndLeaveActionDyna(metaDataDyna);
      sendEnterAndLeaveAction(
        'parent work item',
        parentWorkId,
        setParentWorkId,
        metaData,
      );
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedAlert]);
};

/**
 *
 * @description This function is used in combination with dtrum.
 * If dtrum is not available, it will log an error.
 * It will call the enterAction function from dtrum with the actionTitle and return the actionId.
 * If there is a currentId, it will call the leaveAction function from dtrum with the currentId.
 * If there is a setter function, it will call the setter function with the actionId.
 * If there is no setter function, it will return the actionId.
 * @param actionTitle - The title of the action to be sent to dtrum.enterAction
 * @param currentId - The current actionId to be sent back to the dtrum.leaveAction
 * @param setter - The function to set the actionId in state, this is often a useState setter function. IF not provided, the function will return the actionId to be used outside of this function
 * @param metaData - The metaData object to be stringified and added to the actionTitle then sent to dtrum.enterAction
 */
const sendEnterAndLeaveAction = (
  actionTitle: string,
  currentId: number | null,
  setter: Dispatch<SetStateAction<number | null>>,
  metaData: MetaData,
) => {
  const dtrum = globalThis.dtrum;

  if (!dtrum) {
    console.error('there is no dtrum object');
    return;
  }

  const actionId = sendEnterAction(actionTitle, metaData);
  if (currentId && currentId !== actionId) {
    sendLeaveAction(currentId);
  } else {
    setter(actionId);
  }
};
export const getUnderwriterStatusLabel = (
  statusId: number | undefined,
): string | undefined => {
  switch (statusId) {
    case 1:
      return 'UW Uncomplete';
    case 2:
      return 'UW Partially Verified';
    case 4:
      return 'UW Complete';
    default:
      return undefined;
  }
};

export const getBrokerStatusLabel = (
  statusId: number | undefined,
): string | undefined => {
  switch (statusId) {
    case 1:
      return 'Broker Uncomplete';
    case 2:
      return 'Broker Verified';
    default:
      return undefined;
  }
};

export default useWorkTracking;
